<div class="treatment-plan-container">
  <!-- Loading state -->
  <div class="loading-container" *ngIf="loading">
    <mat-spinner [diameter]="50" color="primary"></mat-spinner>
    <p class="loading-text">Loading your treatment plan...</p>
  </div>

  <!-- Main content when loaded -->
  <div class="content-container" *ngIf="!loading">
    <!-- No treatment plan message -->
    <div class="no-plan-container" *ngIf="!activeTreatmentPlan">
      <mat-icon class="no-plan-icon">healing</mat-icon>
      <h2>No Treatment Plan Available</h2>
      <p *ngIf="noPlansMessage">{{noPlansMessage}}</p>
      <p *ngIf="!noPlansMessage">
        You don't have any active treatment plans at this time. Please contact our office if you have any questions.
      </p>
      
      <!-- Add contact button for no plans scenario -->
      <div class="no-plan-actions">
        <button mat-raised-button color="primary" (click)="contactOffice()">
          <mat-icon>phone</mat-icon> Contact Our Office
        </button>
      </div>
    </div>

    <!-- Treatment plan content -->
    <div class="plan-container" id="treatment-plan-content" *ngIf="activeTreatmentPlan">
      <!-- Header section with summary -->
      <div class="plan-header">
        <div class="plan-title-section">
          <h1 class="plan-title">Your Treatment Plan</h1>
          <span class="plan-status" [ngClass]="getStatusClass(activeTreatmentPlan.status)">
            {{activeTreatmentPlan.status}}
          </span>
        </div>
        
        <div class="plan-actions">
          <button mat-stroked-button class="download-button" (click)="generatePdf()">
            <mat-icon>download</mat-icon> Download Plan
          </button>
        </div>
      </div>

      <!-- Plan details section -->
      <div class="plan-details">
        <p class="plan-note" *ngIf="activeTreatmentPlan.note">
          {{activeTreatmentPlan.note}}
        </p>

        <!-- Summary cards -->
        <div class="summary-section">
          <h2>Plan Summary</h2>
          <div class="summary-cards">
            <div class="summary-card">
              <div class="summary-title">Total Treatment Cost</div>
              <div class="summary-value">{{calculateTotalCost() | currency}}</div>
            </div>
            <div class="summary-card">
              <div class="summary-title">Insurance Estimate</div>
              <div class="summary-value">{{calculateInsuranceEstimate() | currency}}</div>
            </div>
            <div class="summary-card highlight">
              <div class="summary-title">Your Estimated Responsibility</div>
              <div class="summary-value">{{calculatePatientResponsibility() | currency}}</div>
            </div>
          </div>
        </div>

        <!-- Procedures section -->
        <div class="procedures-section" *ngIf="procedures && procedures.length > 0">
          <h2>Recommended Procedures</h2>
          
          <!-- Toggle view button for desktop only -->
          <div class="view-toggle" *ngIf="!isMobile">
            <button mat-raised-button (click)="toggleView()">
              <mat-icon>{{isCompactView ? 'unfold_more' : 'unfold_less'}}</mat-icon>
              {{isCompactView ? 'Show More Details' : 'Show Less Details'}}
            </button>
          </div>

          <!-- Mobile view: Cards -->
          <div class="procedures-cards" *ngIf="isMobile">
            <div *ngFor="let procedure of procedures" class="procedure-card" [ngClass]="getPriorityClass(procedure.priority)">
              <div class="procedure-header">
                <div class="procedure-name">{{procedure.procedureDescription}}</div>
                <div class="procedure-status" [ngClass]="getStatusClass(procedure.procedureStatus)">{{procedure.procedureStatus}}</div>
              </div>
              
              <div class="procedure-details">
                <!-- Basic information always shown -->
                <div class="procedure-detail">
                  <span class="detail-label">When:</span>
                  <span class="detail-value">{{formatDate(procedure.procedureDate)}}</span>
                </div>
                
                <div class="procedure-detail">
                  <span class="detail-label">Priority:</span>
                  <span class="detail-value priority-badge" [ngClass]="getPriorityClass(procedure.priority)">
                    {{procedure.priority}}
                  </span>
                </div>
                
                <div class="procedure-detail">
                  <span class="detail-label">Treatment Area:</span>
                  <span class="detail-value">{{getLocationDescription(procedure)}}</span>
                </div>
                
                <!-- Financial information -->
                <div class="procedure-financials">
                  <div class="financial-row">
                    <span class="financial-label">Total Fee:</span>
                    <span class="financial-value">{{procedure.procedureFee | currency}}</span>
                  </div>
                  
                  <div class="financial-row" *ngIf="procedure.primaryInsAmount">
                    <span class="financial-label">Insurance Covers:</span>
                    <span class="financial-value">{{procedure.primaryInsAmount | currency}}</span>
                  </div>
                  
                  <div class="financial-row highlight">
                    <span class="financial-label">Your Responsibility:</span>
                    <span class="financial-value">{{procedure.patientPortion | currency}}</span>
                  </div>
                </div>
                
                <!-- Code reference for patients who want to check with insurance -->
                <div class="procedure-code">Procedure Code: {{procedure.procedureCode}}</div>
              </div>
            </div>
          </div>

          <!-- Desktop view: Table -->
          <div class="procedures-table-container" *ngIf="!isMobile">
            <table mat-table [dataSource]="dataSource" class="procedures-table">
              <!-- Description Column -->
              <ng-container matColumnDef="procedureDescription">
                <th mat-header-cell *matHeaderCellDef>Treatment</th>
                <td mat-cell *matCellDef="let procedure">
                  <div class="procedure-cell-content">
                    <span class="procedure-title">{{procedure.procedureDescription}}</span>
                    <span class="procedure-code-small">Code: {{procedure.procedureCode}}</span>
                  </div>
                </td>
              </ng-container>

              <!-- Date Column -->
              <ng-container matColumnDef="procedureDate">
                <th mat-header-cell *matHeaderCellDef>When</th>
                <td mat-cell *matCellDef="let procedure">{{formatDate(procedure.procedureDate)}}</td>
              </ng-container>

              <!-- Location Column -->
              <ng-container matColumnDef="location">
                <th mat-header-cell *matHeaderCellDef>Location</th>
                <td mat-cell *matCellDef="let procedure">{{getLocationDescription(procedure)}}</td>
              </ng-container>

              <!-- Priority Column -->
              <ng-container matColumnDef="priority">
                <th mat-header-cell *matHeaderCellDef>Priority</th>
                <td mat-cell *matCellDef="let procedure">
                  <span class="priority-badge" [ngClass]="getPriorityClass(procedure.priority)">{{procedure.priority}}</span>
                </td>
              </ng-container>

              <!-- Status Column -->
              <ng-container matColumnDef="procedureStatus">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let procedure">
                  <span class="status-badge" [ngClass]="getStatusClass(procedure.procedureStatus)">{{procedure.procedureStatus}}</span>
                </td>
              </ng-container>

              <!-- Fee Column -->
              <ng-container matColumnDef="procedureFee">
                <th mat-header-cell *matHeaderCellDef>Total Fee</th>
                <td mat-cell *matCellDef="let procedure">{{procedure.procedureFee | currency}}</td>
              </ng-container>

              <!-- Insurance Column -->
              <ng-container matColumnDef="insurance">
                <th mat-header-cell *matHeaderCellDef>Insurance Covers</th>
                <td mat-cell *matCellDef="let procedure">{{(procedure.primaryInsAmount + procedure.secondaryInsAmount) | currency}}</td>
              </ng-container>

              <!-- Patient Portion Column -->
              <ng-container matColumnDef="patientPortion">
                <th mat-header-cell *matHeaderCellDef>Your Cost</th>
                <td mat-cell *matCellDef="let procedure" class="highlight-text">{{procedure.patientPortion | currency}}</td>
              </ng-container>

              <!-- Advanced Fields - only shown in detailed view -->
              <ng-container matColumnDef="toothNum" *ngIf="!isCompactView">
                <th mat-header-cell *matHeaderCellDef>Tooth #</th>
                <td mat-cell *matCellDef="let procedure">{{procedure.toothNum || '-'}}</td>
              </ng-container>

              <ng-container matColumnDef="surfaces" *ngIf="!isCompactView">
                <th mat-header-cell *matHeaderCellDef>Surfaces</th>
                <td mat-cell *matCellDef="let procedure">{{procedure.surfaces || '-'}}</td>
              </ng-container>

              <ng-container matColumnDef="allowed" *ngIf="!isCompactView">
                <th mat-header-cell *matHeaderCellDef>Allowed Amount</th>
                <td mat-cell *matCellDef="let procedure">{{procedure.allowed | currency}}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
            </table>
          </div>
        </div>

        <!-- Message for when there are no procedures -->
        <div class="no-procedures-message" *ngIf="!procedures || procedures.length === 0">
          <mat-icon>info</mat-icon>
          <p>No procedures have been added to this treatment plan yet.</p>
        </div>

        <!-- Explanations section -->
        <div class="explanations-section" *ngIf="procedures && procedures.length > 0">
          <h3>Understanding Your Treatment Plan</h3>
          <div class="explanation-panels">
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>About Treatment Priorities</mat-panel-title>
              </mat-expansion-panel-header>
              <div class="explanation-content">
                <p><strong>High Priority:</strong> These treatments are recommended to be completed as soon as possible, to address pain, infection, or conditions that may worsen quickly.</p>
                <p><strong>Medium Priority:</strong> These treatments should be scheduled in the near future.</p>
                <p><strong>Low Priority:</strong> These are often elective or cosmetic procedures and can be planned at your convenience.</p>
              </div>
            </mat-expansion-panel>
            
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>About Insurance Estimates</mat-panel-title>
              </mat-expansion-panel-header>
              <div class="explanation-content">
                <p>Insurance coverage amounts shown are estimates based on your current insurance and our past experience with your provider. Actual coverage may vary.</p>
                <p>We recommend contacting your insurance provider directly for detailed coverage questions.</p>
              </div>
            </mat-expansion-panel>
          </div>
        </div>

        <!-- Contact section -->
        <div class="contact-section">
          <h3>Questions About Your Treatment Plan?</h3>
          <p>Our team is available to discuss your plan, explain procedures, or help schedule your next appointment.</p>
          <button mat-raised-button color="primary" (click)="contactOffice()">
            <mat-icon>phone</mat-icon> Contact Our Office
          </button>
        </div>
      </div>
    </div>
  </div>
</div>